import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdQuiz,
  MdAdd,
  MdDelete,
  MdArrowBack,
  MdArrowForward,
  MdCheck,
  MdEdit,
} from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import './Assessment.css';

const STEPS = [
  { id: 1, label: 'Details' },
  { id: 2, label: 'Questions' },
  { id: 3, label: 'Assign & Publish' },
];

const TYPE_LABELS = {
  single_select: 'Single Select',
  mcq: 'Multiple Select',
  essay: 'Essay / Writing',
  case_study: 'Case Study',
};

const emptyQuestion = () => ({
  type: 'single_select',
  text: '',
  marks: 1,
  options: [
    { text: '', isCorrect: true },
    { text: '', isCorrect: false },
  ],
});

/** Format a Date for <input type="datetime-local"> in local timezone */
const toDatetimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const mapQuestionFromApi = (q) => ({
  type: q.type === 'text_input' ? 'essay' : q.type,
  text: q.text || '',
  marks: Number(q.marks) || 0,
  options:
    q.type === 'single_select' || q.type === 'mcq'
      ? (q.options || []).map((o) => ({
          text: o.text || '',
          isCorrect: Boolean(o.isCorrect),
        }))
      : [],
});

const validateQuestion = (q) => {
  if (!String(q.text || '').trim()) return 'Enter the question text';
  if (Number(q.marks) < 0 || Number.isNaN(Number(q.marks))) {
    return 'Enter valid marks';
  }
  if (q.type === 'single_select' || q.type === 'mcq') {
    const options = q.options || [];
    if (options.length < 2) return 'Add at least 2 options';
    if (options.some((o) => !String(o.text || '').trim())) {
      return 'Fill in all option texts';
    }
    const correct = options.filter((o) => o.isCorrect).length;
    if (q.type === 'single_select' && correct !== 1) {
      return 'Mark exactly one correct option';
    }
    if (q.type === 'mcq' && correct < 1) {
      return 'Mark at least one correct option';
    }
  }
  return null;
};

const AssessmentCreate = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [step, setStep] = useState(1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [emailDraft, setEmailDraft] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    mode: 'private',
    durationMinutes: 30,
    scheduledAt: '',
    password: '',
    assignedTo: [],
    attendeeEmails: [],
    questions: [emptyQuestion()],
  });

  useEffect(() => {
    api
      .get('/users/list')
      .then(({ data }) => {
        const list = (data.data || []).filter(
          (u) => String(u._id || u.id) !== String(user?._id || user?.id),
        );
        setUsers(list);
      })
      .catch(() => toast.error('Failed to load users'));
  }, [user]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      setLoadingEdit(true);
      try {
        const { data } = await api.get(`/assessments/${editId}?as=creator`);
        if (cancelled) return;
        if (data.role !== 'creator') {
          toast.error('You cannot edit this assessment');
          navigate('/assessments/created', { replace: true });
          return;
        }
        if (data.editable === false) {
          toast.error(
            'Cannot edit: an assigned user has already started this assessment',
          );
          navigate(`/assessments/${editId}/review`, { replace: true });
          return;
        }
        const a = data.data;
        setForm({
          title: a.title || '',
          description: a.description || '',
          mode: a.mode === 'public' ? 'public' : 'private',
          durationMinutes: a.durationMinutes || 30,
          scheduledAt: toDatetimeLocalValue(a.scheduledAt),
          password: '',
          assignedTo: (a.assignedTo || []).map((u) => String(u._id || u)),
          attendeeEmails: Array.isArray(a.publicEmails)
            ? a.publicEmails.map((e) => String(e).toLowerCase())
            : [],
          questions:
            (a.questions || []).length > 0
              ? a.questions.map(mapQuestionFromApi)
              : [emptyQuestion()],
        });
        setActiveIndex(0);
        setStep(1);
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message || 'Failed to load assessment',
          );
          navigate('/assessments/created', { replace: true });
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, navigate]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleAssignee = (id) => {
    setForm((f) => {
      const has = f.assignedTo.includes(id);
      return {
        ...f,
        assignedTo: has
          ? f.assignedTo.filter((x) => x !== id)
          : [...f.assignedTo, id],
      };
    });
  };

  const current = form.questions[activeIndex] || emptyQuestion();

  const updateCurrent = (patch) => {
    setForm((f) => {
      const questions = f.questions.map((q, i) =>
        i === activeIndex ? { ...q, ...patch } : q,
      );
      return { ...f, questions };
    });
  };

  const changeType = (type) => {
    setForm((f) => {
      const questions = f.questions.map((q, i) => {
        if (i !== activeIndex) return q;
        if (type === 'mcq' || type === 'single_select') {
          return {
            ...q,
            type,
            options:
              q.options?.length >= 2
                ? q.options
                : [
                    { text: '', isCorrect: true },
                    { text: '', isCorrect: false },
                  ],
          };
        }
        return { ...q, type, options: [] };
      });
      return { ...f, questions };
    });
  };

  const updateOption = (oi, patch) => {
    setForm((f) => {
      const questions = f.questions.map((q, i) => {
        if (i !== activeIndex) return q;
        let options = (q.options || []).map((o, j) =>
          j === oi ? { ...o, ...patch } : o,
        );
        if (q.type === 'single_select' && patch.isCorrect === true) {
          options = options.map((o, j) => ({
            ...o,
            isCorrect: j === oi,
          }));
        }
        return { ...q, options };
      });
      return { ...f, questions };
    });
  };

  const addOption = () => {
    updateCurrent({
      options: [...(current.options || []), { text: '', isCorrect: false }],
    });
  };

  const removeOption = (oi) => {
    const options = (current.options || []).filter((_, j) => j !== oi);
    updateCurrent({ options });
  };

  const goToQuestion = (i) => {
    const err = validateQuestion(current);
    if (err && i !== activeIndex) {
      toast.error(`Question ${activeIndex + 1}: ${err}`);
      return;
    }
    setActiveIndex(i);
  };

  const questionMarksSum = form.questions.reduce(
    (s, q) => s + (Number(q.marks) || 0),
    0,
  );

  const addNextQuestion = () => {
    const err = validateQuestion(current);
    if (err) {
      toast.error(err);
      return;
    }
    setForm((f) => ({
      ...f,
      questions: [...f.questions, emptyQuestion()],
    }));
    setActiveIndex(form.questions.length);
    toast.success(`Question ${form.questions.length} saved. Add the next one.`);
  };

  const removeCurrentQuestion = () => {
    if (form.questions.length <= 1) {
      toast.error('Keep at least one question');
      return;
    }
    if (!window.confirm(`Remove question ${activeIndex + 1}?`)) return;
    setForm((f) => {
      const questions = f.questions.filter((_, i) => i !== activeIndex);
      return { ...f, questions };
    });
    setActiveIndex((i) => Math.max(0, Math.min(i, form.questions.length - 2)));
  };

  const addAttendeeEmail = () => {
    const email = String(emailDraft || '').trim().toLowerCase();
    if (!email) return;
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (form.attendeeEmails.includes(email)) {
      toast.error('Email already added');
      return;
    }
    setForm((f) => ({
      ...f,
      attendeeEmails: [...f.attendeeEmails, email],
    }));
    setEmailDraft('');
  };

  const removeAttendeeEmail = (email) => {
    setForm((f) => ({
      ...f,
      attendeeEmails: f.attendeeEmails.filter((e) => e !== email),
    }));
  };

  const goNextFromDetails = () => {
    if (!form.title.trim()) {
      toast.error('Enter an assessment title');
      return;
    }
    if (form.mode !== 'public' && form.mode !== 'private') {
      toast.error('Select assessment mode');
      return;
    }
    setStep(2);
  };

  const goNextFromQuestions = () => {
    const err = validateQuestion(current);
    if (err) {
      toast.error(`Question ${activeIndex + 1}: ${err}`);
      return;
    }
    for (let i = 0; i < form.questions.length; i += 1) {
      const qErr = validateQuestion(form.questions[i]);
      if (qErr) {
        toast.error(`Question ${i + 1}: ${qErr}`);
        setActiveIndex(i);
        return;
      }
    }
    if (questionMarksSum <= 0) {
      toast.error('Total marks must be greater than 0');
      return;
    }
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!isEdit && (!form.password || form.password.length < 4)) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (
      isEdit &&
      form.password &&
      form.password.length > 0 &&
      form.password.length < 4
    ) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (!form.durationMinutes || form.durationMinutes < 1) {
      toast.error('Enter duration in minutes');
      return;
    }
    if (!form.scheduledAt) {
      toast.error('Select the assessment date and time');
      return;
    }
    const scheduledISO = new Date(form.scheduledAt).toISOString();
    if (Number.isNaN(new Date(scheduledISO).getTime())) {
      toast.error('Please choose a valid assessment date and time');
      return;
    }
    if (form.mode === 'public') {
      if (!form.attendeeEmails.length) {
        toast.error('Add at least one attendee email');
        return;
      }
    } else if (!form.assignedTo.length) {
      toast.error('Assign at least one staff member');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        mode: form.mode,
        durationMinutes: form.durationMinutes,
        scheduledAt: scheduledISO,
        questions: form.questions,
        totalMarks: questionMarksSum,
      };
      if (form.mode === 'public') {
        payload.attendeeEmails = form.attendeeEmails;
      } else {
        payload.assignedTo = form.assignedTo;
      }
      if (form.password) payload.password = form.password;

      if (isEdit) {
        const { data } = await api.put(`/assessments/${editId}`, payload);
        toast.success(data.message || 'Assessment updated');
        navigate(`/assessments/${editId}/review`);
      } else {
        const { data } = await api.post('/assessments', {
          ...payload,
          password: form.password,
        });
        toast.success(data.message || 'Assessment created');
        navigate(`/assessments/${data.data._id}/review`);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (isEdit
            ? 'Could not update assessment'
            : 'Could not create assessment'),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loadingEdit) {
    return (
      <div className="assessment-page">
        <div className="assessment-loading">
          <span className="spinner" /> Loading assessment…
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-page assessment-page-wide">
      <div className="assessment-header">
        <div>
          <h1>
            <MdQuiz size={28} />
            {isEdit ? 'Edit Assessment' : 'Create Assessment'}
          </h1>
          <p className="assessment-subtitle">
            {isEdit
              ? 'You can edit details and questions until an assignee starts the assessment.'
              : 'Step-by-step: details → questions (one at a time) → assign & publish.'}
          </p>
        </div>
        <Link to="/assessments/created" className="assessment-btn secondary">
          Cancel
        </Link>
      </div>

      <div className="assessment-wizard-steps" aria-label="Progress">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={`assessment-wizard-step ${
              step === s.id ? 'active' : ''
            } ${step > s.id ? 'done' : ''}`}
          >
            <span className="assessment-wizard-step-num">
              {step > s.id ? <MdCheck size={16} /> : s.id}
            </span>
            <span className="assessment-wizard-step-label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="assessment-form">
        {step === 1 && (
          <>
            <h2 className="assessment-wizard-heading">Assessment details</h2>
            <p className="assessment-subtitle" style={{ marginBottom: 20 }}>
              Choose who can take this assessment, then add the title and
              instructions.
            </p>
            <div className="assessment-form-grid">
              <div className="assessment-field assessment-field-full">
                <label>Assessment mode</label>
                <div className="assessment-mode-cards">
                  <button
                    type="button"
                    className={`assessment-mode-card ${
                      form.mode === 'private' ? 'selected' : ''
                    }`}
                    disabled={isEdit}
                    onClick={() => setField('mode', 'private')}
                  >
                    <strong>Private</strong>
                    <span>
                      Assign to registered staff in the system. They take it
                      while logged in.
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`assessment-mode-card ${
                      form.mode === 'public' ? 'selected' : ''
                    }`}
                    disabled={isEdit}
                    onClick={() => setField('mode', 'public')}
                  >
                    <strong>Public</strong>
                    <span>
                      Invite people by email. Each gets a secure personal link
                      to attempt the assessment.
                    </span>
                  </button>
                </div>
                {isEdit && (
                  <span className="assessment-field-hint">
                    Mode cannot be changed after creation.
                  </span>
                )}
              </div>
              <div className="assessment-field assessment-field-full">
                <label>Title</label>
                <input
                  autoFocus
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  placeholder="e.g. Q2 Product Knowledge Test"
                />
              </div>
              <div className="assessment-field assessment-field-full">
                <label>Description / instructions (optional)</label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="What attendees should know before starting…"
                />
              </div>
            </div>
            <div className="assessment-wizard-footer">
              <span />
              <button
                type="button"
                className="assessment-btn"
                onClick={goNextFromDetails}
              >
                Next: Add questions
                <MdArrowForward size={18} />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="assessment-wizard-q-top">
              <div>
                <h2 className="assessment-wizard-heading">
                  Question {activeIndex + 1} of {form.questions.length}
                </h2>
                <p className="assessment-subtitle">
                  Total marks so far: <strong>{questionMarksSum}</strong>
                  {' · '}
                  {form.title}
                </p>
              </div>
            </div>

            <div className="assessment-q-nav">
              {form.questions.map((q, i) => {
                const valid = !validateQuestion(q);
                return (
                  <button
                    key={i}
                    type="button"
                    className={`assessment-q-nav-item ${
                      i === activeIndex ? 'active' : ''
                    } ${valid ? 'valid' : ''}`}
                    onClick={() => goToQuestion(i)}
                    title={q.text || `Question ${i + 1}`}
                  >
                    {i === activeIndex ? <MdEdit size={12} /> : null}
                    Q{i + 1}
                  </button>
                );
              })}
            </div>

            <div className="assessment-question-block assessment-question-focus">
              <div className="assessment-question-head">
                <strong>Edit question {activeIndex + 1}</strong>
                {form.questions.length > 1 && (
                  <button
                    type="button"
                    className="assessment-btn ghost"
                    onClick={removeCurrentQuestion}
                  >
                    <MdDelete size={16} />
                    Remove this question
                  </button>
                )}
              </div>

              <div className="assessment-form-grid">
                <div className="assessment-field">
                  <label>Type</label>
                  <select
                    value={current.type}
                    onChange={(e) => changeType(e.target.value)}
                  >
                    <option value="single_select">Single Select</option>
                    <option value="mcq">Multiple Select</option>
                    <option value="essay">Essay / Writing</option>
                    <option value="case_study">Case Study</option>
                  </select>
                </div>
                <div className="assessment-field">
                  <label>Marks</label>
                  <input
                    type="number"
                    min={0}
                    value={current.marks}
                    onChange={(e) =>
                      updateCurrent({ marks: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="assessment-field assessment-field-full">
                  <label>Question text</label>
                  <textarea
                    rows={3}
                    autoFocus
                    value={current.text}
                    onChange={(e) => updateCurrent({ text: e.target.value })}
                    placeholder="Write the question…"
                  />
                </div>
              </div>

              {(current.type === 'mcq' || current.type === 'single_select') && (
                <div className="assessment-options">
                  <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    Options — mark correct{' '}
                    {current.type === 'mcq' ? 'answer(s)' : 'answer'}
                  </label>
                  {(current.options || []).map((o, oi) => (
                    <div key={oi} className="assessment-option-row">
                      <input
                        type={current.type === 'mcq' ? 'checkbox' : 'radio'}
                        name="correct-active"
                        checked={Boolean(o.isCorrect)}
                        onChange={(e) =>
                          updateOption(oi, { isCorrect: e.target.checked })
                        }
                        title="Correct answer"
                      />
                      <input
                        type="text"
                        placeholder={`Option ${oi + 1}`}
                        value={o.text}
                        onChange={(e) =>
                          updateOption(oi, { text: e.target.value })
                        }
                      />
                      {(current.options || []).length > 2 && (
                        <button
                          type="button"
                          className="assessment-btn ghost"
                          onClick={() => removeOption(oi)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="assessment-btn secondary"
                    onClick={addOption}
                  >
                    Add option
                  </button>
                </div>
              )}
            </div>

            <div className="assessment-wizard-footer assessment-wizard-footer-split">
              <button
                type="button"
                className="assessment-btn secondary"
                onClick={() => setStep(1)}
              >
                <MdArrowBack size={18} />
                Back
              </button>
              <div className="assessment-actions">
                <button
                  type="button"
                  className="assessment-btn secondary"
                  onClick={addNextQuestion}
                >
                  <MdAdd size={18} />
                  Save & add next question
                </button>
                <button
                  type="button"
                  className="assessment-btn"
                  onClick={goNextFromQuestions}
                >
                  Next: Assign & publish
                  <MdArrowForward size={18} />
                </button>
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="assessment-wizard-heading">
              {isEdit ? 'Assign & save' : 'Assign & publish'}
            </h2>
            <p className="assessment-subtitle" style={{ marginBottom: 20 }}>
              {form.mode === 'public'
                ? isEdit
                  ? 'Update schedule, password (optional), and attendee emails. New emails get a secure link.'
                  : 'Set schedule and password, add attendee emails, then create. Each email gets a secure personal link.'
                : isEdit
                  ? 'Update date/time, duration, password (optional), and assignees. New assignees get an email.'
                  : 'Set when the assessment will be given, duration and password, choose staff, then create. Assignees get an email.'}
            </p>

            <div className="assessment-score-strip" style={{ marginTop: 0 }}>
              <span>
                Title: <strong>{form.title}</strong>
              </span>
              <span>
                Mode:{' '}
                <strong>
                  {form.mode === 'public' ? 'Public' : 'Private'}
                </strong>
              </span>
              <span>
                Questions: <strong>{form.questions.length}</strong>
              </span>
              <span>
                Total marks: <strong>{questionMarksSum}</strong>
              </span>
            </div>

            <div className="assessment-form-grid" style={{ marginTop: 18 }}>
              <div className="assessment-field">
                <label>Assessment date &amp; time</label>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => setField('scheduledAt', e.target.value)}
                  required
                />
                <span className="assessment-field-hint">
                  When this assessment will be given
                </span>
              </div>
              <div className="assessment-field">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  min={1}
                  value={form.durationMinutes}
                  onChange={(e) =>
                    setField('durationMinutes', Number(e.target.value))
                  }
                />
              </div>
              <div className="assessment-field">
                <label>
                  Assessment password
                  {isEdit ? ' (leave blank to keep current)' : ''}
                </label>
                <input
                  type="text"
                  minLength={isEdit ? undefined : 4}
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  placeholder={
                    isEdit
                      ? 'Leave blank to keep current password'
                      : 'Share this with attendees'
                  }
                />
              </div>

              {form.mode === 'public' ? (
                <div className="assessment-field assessment-field-full">
                  <label>
                    Attendee emails ({form.attendeeEmails.length} added)
                  </label>
                  <div className="assessment-email-add-row">
                    <input
                      type="email"
                      value={emailDraft}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addAttendeeEmail();
                        }
                      }}
                      placeholder="name@example.com"
                    />
                    <button
                      type="button"
                      className="assessment-btn secondary"
                      onClick={addAttendeeEmail}
                    >
                      <MdAdd size={18} />
                      Add email
                    </button>
                  </div>
                  <span className="assessment-field-hint">
                    Each email receives a secure personal link to take the
                    assessment.
                  </span>
                  {form.attendeeEmails.length > 0 && (
                    <div className="assessment-assignees" style={{ marginTop: 10 }}>
                      {form.attendeeEmails.map((email) => (
                        <button
                          key={email}
                          type="button"
                          className="assessment-assignee-chip selected"
                          onClick={() => removeAttendeeEmail(email)}
                          title="Remove"
                        >
                          {email} ✕
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="assessment-field assessment-field-full">
                  <label>
                    Assign staff ({form.assignedTo.length} selected)
                  </label>
                  <div className="assessment-assignees">
                    {users.map((u) => {
                      const id = String(u._id || u.id);
                      const selected = form.assignedTo.includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          className={`assessment-assignee-chip ${
                            selected ? 'selected' : ''
                          }`}
                          onClick={() => toggleAssignee(id)}
                        >
                          {u.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="assessment-q-summary">
              <h3>Questions summary</h3>
              <ul>
                {form.questions.map((q, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="assessment-q-summary-link"
                      onClick={() => {
                        setActiveIndex(i);
                        setStep(2);
                      }}
                    >
                      Q{i + 1}
                    </button>
                    <span className="assessment-badge">
                      {TYPE_LABELS[q.type] || q.type}
                    </span>
                    <span>{q.marks} marks</span>
                    <span className="assessment-q-summary-text">
                      {q.text || '(empty)'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="assessment-wizard-footer assessment-wizard-footer-split">
              <button
                type="button"
                className="assessment-btn secondary"
                onClick={() => setStep(2)}
              >
                <MdArrowBack size={18} />
                Back to questions
              </button>
              <button
                type="button"
                className="assessment-btn"
                disabled={
                  saving ||
                  (form.mode === 'public'
                    ? form.attendeeEmails.length === 0
                    : form.assignedTo.length === 0)
                }
                title={
                  form.mode === 'public'
                    ? form.attendeeEmails.length === 0
                      ? 'Add at least one email'
                      : undefined
                    : form.assignedTo.length === 0
                      ? 'Select at least one staff member'
                      : undefined
                }
                onClick={handleSubmit}
              >
                {saving
                  ? isEdit
                    ? 'Saving…'
                    : 'Creating…'
                  : isEdit
                    ? 'Save changes'
                    : form.mode === 'public'
                      ? 'Create & email secure links'
                      : 'Create & notify assignees'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssessmentCreate;
